import { loginSchema } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Button, Field, Input } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { fetchMe, meQueryKey } from '@/lib/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const parsed = loginSchema.safeParse(data);
    setFormError(null);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    setPending(true);
    try {
      await api('/auth/login', { method: 'POST', body: parsed.data });
      qc.setQueryData(meQueryKey, await fetchMe());
      const target = search.redirect?.startsWith('/') ? search.redirect : '/';
      await navigate({ to: target });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429)
        setFormError('Слишком много попыток. Подождите минуту и попробуйте снова.');
      else if (err instanceof ApiError) setFormError(err.message);
      else setFormError('Сервер не отвечает. Проверьте подключение к сети.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_minmax(420px,520px)]">
      <section aria-hidden className="relative hidden overflow-hidden bg-graphite lg:block">
        <RailSchematic />
        <div className="absolute bottom-10 left-12 max-w-[34ch] text-[#b9c5ce]">
          <p className="text-[28px] font-semibold leading-tight tracking-[-0.015em] text-white">ДизельПро</p>
          <p className="mt-2">Сделки, задачи, сотрудники и оборудование — в одной системе.</p>
        </div>
      </section>

      <section className="flex items-center justify-center px-6 py-12">
        <form onSubmit={onSubmit} noValidate className="w-full max-w-[340px]">
          <img src="/favicon.svg" alt="" className="mb-6 size-10 lg:hidden" />
          <h1 className="text-[24px] font-semibold tracking-[-0.015em]">Вход</h1>
          <p className="mt-1 mb-7 text-steel">Логин и пароль выдаёт администратор.</p>

          <div className="flex flex-col gap-4">
            <Field label="Логин" error={errors.login}>
              {(p) => <Input {...p} name="login" autoComplete="username" autoFocus spellCheck={false} />}
            </Field>
            <Field label="Пароль" error={errors.password}>
              {(p) => <Input {...p} name="password" type="password" autoComplete="current-password" />}
            </Field>
          </div>

          {formError && (
            <p role="alert" className="mt-4 rounded-md bg-danger-soft px-3 py-2 text-[13.5px] text-danger">
              {formError}
            </p>
          )}

          <Button type="submit" loading={pending} className="mt-6 w-full">
            Войти
          </Button>
        </form>
      </section>
    </div>
  );
}

/** Схема топливной рампы Common Rail: магистраль и форсунки — опознавательный мотив компании. */
function RailSchematic() {
  const injectors = [0, 1, 2, 3, 4, 5];
  return (
    <svg
      viewBox="0 0 800 600"
      className="absolute inset-0 size-full"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label="Схема топливной рампы"
    >
      <defs>
        <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M24 0H0V24" fill="none" stroke="#26343f" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="800" height="600" fill="url(#grid)" />
      <g fill="none" stroke="#3d5060" strokeWidth="2">
        <rect x="120" y="180" width="560" height="44" rx="22" />
        <path d="M80 202h40M680 202h40" />
        {injectors.map((i) => {
          const x = 170 + i * 92;
          return (
            <g key={i}>
              <path d={`M${x} 224v60`} />
              <rect x={x - 14} y="284" width="28" height="74" rx="6" />
              <path d={`M${x} 358v26l-8 14M${x} 384l8 14`} />
            </g>
          );
        })}
      </g>
      <circle cx="400" cy="202" r="7" fill="#f2b705" />
      <path d="M80 202H60v120" fill="none" stroke="#f2b705" strokeWidth="2" strokeDasharray="6 6" />
    </svg>
  );
}
